import { Signal } from "@angular/core";
import {
  apply,
  form,
  maxLength,
  PathKind,
  required,
  Schema,
  schema,
  SchemaPathTree,
  validate,
} from "@angular/forms/signals";
import { Marketplace, SbCreativeType } from "@front/m19-api-client";
import { BrandNameMaxLength } from "@front/m19-models/SbLimits";
import { Constant, LandingPage } from "@front/m19-services";
import { validateDistinctSbAsins, validateSbAsins } from "@m19-board/utils/validators/sbAsinsValidators";
import { validateSbBrandName, validateSbHeadline } from "@m19-board/utils/validators/sbAssetsValidators";
import { SbWizardFormModel } from "./sb-wizard-types";

export function createSbWizardForm(options: {
  formModel: Parameters<typeof form<SbWizardFormModel>>[0];
  isVendor: Signal<boolean>;
  marketplace: Signal<Marketplace>;
  submitForm: () => Promise<void>;
  customSchema?: (schema: SchemaPathTree<SbWizardFormModel, PathKind.Root>) => Schema<SbWizardFormModel>;
}) {
  return form(
    options.formModel,
    (schema) => {
      apply(schema, validateSbWizardFormSetup(options.isVendor()));

      apply(schema, validateSbWizardFormFormat);

      apply(schema, validateSbWizardFormAssets(options.marketplace()));

      apply(schema, validateSbWizardFormCreativesSbAsins);

      if (options.customSchema) {
        apply(schema, options.customSchema(schema));
      }
    },
    {
      submission: {
        action: async () => options.submitForm(),
      },
    },
  );
}

const validateSbWizardFormAssets = (marketplace: Marketplace) =>
  schema<SbWizardFormModel>((schema) => {
    required(schema.assets.brandName, {
      when: ({ valueOf }) => valueOf(schema.format.sbCreativeType) !== SbCreativeType.video,
    });
    maxLength(schema.assets.brandName, BrandNameMaxLength);
    validate(schema.assets.brandName, validateSbBrandName);

    required(schema.assets.headline, {
      when: ({ valueOf }) => {
        const creativeType = valueOf(schema.format.sbCreativeType);
        return (
          creativeType !== SbCreativeType.video &&
          creativeType !== SbCreativeType.manualCollection &&
          creativeType !== SbCreativeType.productCollection
        );
      },
    });
    validate(schema.assets.headline, (ctx) =>
      validateSbHeadline(ctx.valueOf(schema.format.sbCreativeType), marketplace)(ctx),
    );

    required(schema.assets.brandLogo, {
      when: ({ valueOf }) => {
        const creativeType = valueOf(schema.format.sbCreativeType);
        return (
          creativeType !== SbCreativeType.video &&
          creativeType !== SbCreativeType.manualCollection &&
          creativeType !== SbCreativeType.productCollection
        );
      },
    });
    required(schema.assets.video, {
      when: ({ valueOf }) => {
        const creativeType = valueOf(schema.format.sbCreativeType);
        return creativeType === SbCreativeType.video || creativeType === SbCreativeType.brandVideo;
      },
    });
  });

const validateSbWizardFormCreativesSbAsins = schema<SbWizardFormModel>((schema) => {
  required(schema.creativesSbAsins.sbAsins);
  validate(schema.creativesSbAsins.sbAsins, (ctx) => validateSbAsins(ctx, ctx.valueOf(schema.format.sbCreativeType)));
  validate(schema.creativesSbAsins.sbAsins, (ctx) =>
    validateDistinctSbAsins(ctx, ctx.valueOf(schema.format.sbCreativeType)),
  );
});

const validateSbWizardFormFormat = schema<SbWizardFormModel>((schema) => {
  required(schema.format.sbLandingPage);
  required(schema.format.store, {
    when: ({ valueOf }) => {
      const creativeType = valueOf(schema.format.sbCreativeType);
      return valueOf(schema.format.sbLandingPage) === LandingPage.Store && creativeType !== SbCreativeType.video;
    },
  });
});

const validateSbWizardFormSetup = (isVendor: boolean) =>
  schema<SbWizardFormModel>((schema) => {
    required(schema.setup.name);
    maxLength(schema.setup.name, Constant.strategyNameMaxLength);
    required(schema.setup.brand, {
      when: () => !isVendor,
    });
    required(schema.setup.algo);
    validate(schema.setup.algo, ({ value }) => (value()?.isValid ? null : { kind: "invalidAlgo" }));
  });

export type SbWizardForm = ReturnType<typeof createSbWizardForm>;
